import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import styled from "styled-components";
import { rgba, p3, media } from "../styles/stylesTheme";
import { borderRadius, getColor } from "../styles/stylesUtils";
import { getCSSImageUrl } from "../utils/mediaUrl";

const WhatToDoOptions = ({
    onStartStream,
    onAction = null,
    buttonLabel = "Capture captions",
    buttonDetails = null,
    disabled = false,
}) => {
    const handleClick = onAction || onStartStream;

    return (
        <AnimatePresence mode="wait">
            <GridOptionsWrap>
                <GridOptionButton
                    $disabled={disabled}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{
                        opacity: {
                            duration: 0.3,
                            delay: 0.12,
                        },
                    }}
                    onClick={() => {
                        if (!disabled && handleClick) {
                            handleClick();
                        }
                    }}
                >
                    <div>
                        <div className="img"></div>
                        <p>
                            <span>{buttonLabel}</span>
                            {buttonDetails && (
                                <span className="button-details">
                                    {buttonDetails}
                                </span>
                            )}
                        </p>
                    </div>
                </GridOptionButton>
            </GridOptionsWrap>
        </AnimatePresence>
    );
};

const GridOptionsWrap = styled.div`
    position: relative;
    box-shadow: none;
    color: white;
    background: none;
    width: auto;
    float: none;
    margin: 0;
    max-width: none;
    width: 100%;
    position: relative;
    margin-bottom: 2em;
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: 1.4em;
    padding: 0 3em;
    box-sizing: border-box;
    max-width: 20em;
    margin-left: auto;
    margin-right: auto;

    &:hover {
        > div {
            transition: 0.3s;

            > div {
                opacity: 0.8;
                transition: 0.3s;

                &:hover {
                    opacity: 1;

                    .img {
                        opacity: 1;
                    }
                }
            }

            &:hover {
                background: ${getColor("colorYou", 1)};
                box-shadow: 0 0.1em 0 0 ${getColor("colorAccent", 0.2)},
                    0 0 0 0.1em ${getColor("white", 0.5)},
                    0 0.1em 0.1em 0 rgba(0, 0, 0, 0.05);
                cursor: pointer;
            }

            &:active {
                transform: scale(0.993) translateY(1.5px);
                background: ${getColor("colorYou", 1, -10)};
                box-shadow: 0 0 0 0.1em ${getColor("white", 0.5)},
                    0 0.1em 0.1em 0 rgba(0, 0, 0, 0.05);
            }
        }
    }

    ${media.mobile} {
        grid-template-columns: repeat(1, 1fr);
        gap: 0.8em;
        padding: 0;
        max-width: 14em;
    }
`;

const GridOptionButton = styled(motion.div)`
    background: ${(props) => rgba(props.theme, "colorYou", 1)};
    background: ${(props) => p3(props.theme, "pcolorYou", 1)};
    ${borderRadius(0.8)};
    width: 100%;
    height: 12em;
    text-align: center;
    margin: 0;
    overflow: hidden;
    cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
    opacity: ${({ $disabled }) => ($disabled ? 0.65 : 1)};
    pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
    position: relative;
    box-shadow: 0 0.1em 0 0 ${getColor("colorAccent", 0.2)};

    > div {
        height: 100%;
        width: 100%;
        margin: 0;
        transition: 150ms;

        > div.img {
            width: 100%;
            height: 9.4em;
            background: ${(props) => rgba(props.theme, "white", 0.2)};
            position: relative;
            transition: 0.15s;

            &:before {
                content: "";
                position: absolute;
                left: 50%;
                top: 50%;
                transform: translate(-50%, -50%);
                width: 3.4em;
                height: 3em;
                background: ${({ theme }) => rgba(theme, "colorCorrect", 1)};
                background: ${({ theme }) => p3(theme, "pcolortextYou", 1)};
                mask: url("${getCSSImageUrl("img/icon-captions.svg")}") no-repeat center / contain;
                -webkit-mask: url("${getCSSImageUrl("img/icon-captions.svg")}") no-repeat center /
                    contain;
                mask-size: 100%;
                -webkit-mask-size: 100%;
            }
        }

        p {
            line-height: 1.6em;
            vertical-align: top;
            margin-bottom: 0px;
            text-align: center;
            padding: 0.9em 0.2em 1em;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            margin: 0;
            color: ${(props) => rgba(props.theme, "colortextYou", 1)};
            color: ${(props) => p3(props.theme, "pcolortextYou", 1)};
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            height: 2em;
            z-index: 10;
            position: absolute;
            background: ${(props) => rgba(props.theme, "colorYou", 1)};
            background: ${(props) => p3(props.theme, "pcolorYou", 1)};
            backdrop-filter: blur(1.5em);
            -webkit-backdrop-filter: blur(1.5em);
            white-space: normal;
            overflow: visible;
            height: auto;
            bottom: 0;
            width: 100%;
            box-sizing: border-box;
            ${borderRadius(0, 0, 0.8, 0.8)};

            .button-details {
                font-family: var(--normal);
                font-size: 0.62em;
                line-height: 1.2em;
                opacity: 0.7;
            }
        }
    }

    ${media.mobile} {
        font-size: 70%;
        ${borderRadius(1)};
        height: auto;
        aspect-ratio: 9 / 12;

        > div {
            > div.img {
                height: 75%;

                &:before {
                    width: 36%;
                    height: 36%;
                }
            }
        }

        > div {
            p {
                font-size: 1.4em;
                height: 30%;
                white-space: nowrap;
            }
        }
    }
`;

export default WhatToDoOptions;
